// @ts-nocheck
import React from "react";
import { createPortal } from "react-dom";
import HelpTip from "../../../components/HelpTip";
import EmptyState from "../../../components/EmptyState";
import MobileAssetList from "../../../components/MobileAssetList";
import MobileAssetDrawer from "../../../components/MobileAssetDrawer";
import {
  cancelAnalysis,
  disableAsset,
  enableAsset,
  retryAnalysis,
} from "../../../api";
import { formatDate } from "../../../utils/formatDate";
import { hasPermission } from "../../../utils/permissions";
import {
  AddNewElementRow,
  CharacterGroup,
  ScenePropGroup,
  tryParseJSON,
} from "./AnalysisElements";
import AssetSortMenu from "./AssetSortMenu";
import {
  DEFAULT_FIXED_IMAGE_PROMPT,
  parseRefImages,
} from "../utils/editDefaults";
import type { CharacterVoice } from "../../../utils/characterVoice";

type ProjectAnalysisPanelProps = {
  characterVoiceMap: Map<string, CharacterVoice>;
  analysisAuthorityRevision: number;
  [key: string]: any;
};

export default function ProjectAnalysisPanel(panelProps: ProjectAnalysisPanelProps) {
  const {
    mode,
    id,
    analysis,
    analysisAuthorityRevision,
    project,
    characterImageWorkflow,
    currentUser,
    analysisTab,
    setAnalysisTab,
    analysisExpanded,
    t,
    episodes,
    handleStartAnalysis,
    retryingAnalysis,
    setRetryingAnalysis,
    cancellingAnalysis,
    setCancellingAnalysis,
    setAnalysis,
    toast,
    charGroups,
    characterVoiceMap,
    sceneGroups,
    propGroups,
    setAssetShareForm,
    setAssetShareModal,
    openShareHistory,
    searchIsActive,
    isGeneratingKeywords,
    placeholderTimedOut,
    searchActiveCount,
    searchQueuedCount,
    searchingCount,
    searchSummary,
    searchTasks,
    triggeringSearch,
    setConfirmResearchOpen,
    setConfirmCategorySearch,
    searchDetailExpanded,
    activeSearchDetails,
    setConfirmBatchSearch,
    isMobile,
    filteredCharGroups,
    filteredSceneGroups,
    filteredPropGroups,
    allAssetEpisodes,
    filterEpisodes,
    setFilterEpisodes,
    assetSortMode,
    setAssetSortMode,
    props,
    activeNavId,
    mobileDrawerAsset,
    setMobileDrawerAsset,
    activeTaskMap,
    failedTaskMap,
    baseActiveTaskMap,
    baseFailedTaskMap,
    onImageTaskResolved,
    episodeMap,
    loadAnalysis,
    openSearchSelectModal,
    getSearchImageCount,
    openSearchSelectModalAllStates,
    getSearchImageCountAllStates,
    getSearchTaskProgress,
    getSearchTaskProgressAllStates,
    handleElementUpdate,
    handleCharAddState,
    handleSceneAddState,
    handlePropAddState,
    disableAssetLoading,
    setDisableAssetLoading,
    setDisableAssetPending,
    setDisableAssetAffectedCount,
    setDisableAssetAffectedDetails,
    setDisableAssetStep,
    setProject,
    setRenameTarget,
    setCommentTarget,
    pendingCommentCountMap,
    isRealPerson,
    reviewStatusMap,
    loadProjectReviews,
    availableEpisodeOptions,
    handleElementEpisodesUpdate,
    handleAddNewElement,
    addingNewChar,
    setAddingNewChar,
    addingNewScene,
    setAddingNewScene,
    addingNewProp,
    setAddingNewProp,
    onGenerateKeywords,
    getKeywordStatus,
    onSubmitSearch,
    activeImageModels,
  } = panelProps;
  const effectiveCharacterImageWorkflow =
    characterImageWorkflow || project?.characterImageWorkflow;

  // 剧集筛选下拉（多选）：点击外部关闭
  const [episodeFilterOpen, setEpisodeFilterOpen] = React.useState(false);
  const episodeFilterRef = React.useRef<HTMLDivElement | null>(null);
  // 弹层通过 portal 渲染到 body，避开外层 .theme-left-border 卡片的 overflow:clip 裁剪
  // （资产被剧集筛选后变少时卡片变矮，绝对定位弹层会被卡片底边裁掉，导致剧集显示不全）
  const episodeFilterTriggerRef = React.useRef<HTMLButtonElement | null>(null);
  const episodeFilterMenuRef = React.useRef<HTMLDivElement | null>(null);
  const [episodeFilterPos, setEpisodeFilterPos] = React.useState<{
    top: number;
    right: number;
    maxHeight: number;
  } | null>(null);
  const recomputeEpisodeFilterPos = React.useCallback(() => {
    const el = episodeFilterTriggerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const top = rect.bottom + 4;
    setEpisodeFilterPos({
      top,
      right: Math.max(8, window.innerWidth - rect.right),
      maxHeight: Math.max(160, Math.min(300, window.innerHeight - top - 12)),
    });
  }, []);
  React.useEffect(() => {
    if (!episodeFilterOpen) return;
    const onClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      const inTrigger = episodeFilterRef.current?.contains(target);
      const inMenu = episodeFilterMenuRef.current?.contains(target);
      if (!inTrigger && !inMenu) setEpisodeFilterOpen(false);
    };
    const onReposition = () => recomputeEpisodeFilterPos();
    document.addEventListener("mousedown", onClickOutside);
    // 捕获阶段监听，任意可滚动祖先滚动时都能重新定位
    window.addEventListener("scroll", onReposition, true);
    window.addEventListener("resize", onReposition);
    return () => {
      document.removeEventListener("mousedown", onClickOutside);
      window.removeEventListener("scroll", onReposition, true);
      window.removeEventListener("resize", onReposition);
    };
  }, [episodeFilterOpen, recomputeEpisodeFilterPos]);
  const toggleFilterEpisode = (ep: number) => {
    setFilterEpisodes(
      filterEpisodes.includes(ep)
        ? filterEpisodes.filter((n: number) => n !== ep)
        : [...filterEpisodes, ep].sort((a: number, b: number) => a - b),
    );
  };
  const episodeFilterLabel =
    filterEpisodes.length === 0
      ? t("analysis.allEpisodes")
      : filterEpisodes.length === 1
        ? t("analysis.episodeN", { n: filterEpisodes[0] })
        : t("analysis.selectedEpisodes", { count: filterEpisodes.length });

  return (
    <>
      {(mode === "basic" ||
        mode === "advanced" ||
        (mode === "external" && analysis?.status === "completed")) &&
        (analysis || project?.script?.status === "completed") && (
          <div
            className="card theme-left-border"
            id="analysis-section-top"
            style={
              {
                borderRadius: "10px",
                "--_theme-color": `var(--theme-${analysisTab === "character" ? "character" : analysisTab === "scene" ? "scene" : "prop"})`,
                "--_theme-color-end": "#60a5fa",
              } as React.CSSProperties
            }
          >
            <div
              style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "0.75rem",
                  flex: 1,
                  minWidth: 0,
                  overflow: "hidden",
                  lineHeight: 1.4,
                }}
              >
                <h3
                  style={{
                    margin: 0,
                    lineHeight: "inherit",
                    display: "inline-flex",
                    alignItems: "center",
                    color: `var(--theme-${analysisTab === "character" ? "character" : analysisTab === "scene" ? "scene" : "prop"})`,
                  }}
                >
                  {t("analysis.title")}
                  <span
                    style={{
                      fontSize: "calc(0.65rem + 3px)",
                      fontWeight: 500,
                      marginLeft: "0.4rem",
                      padding: "1px 6px",
                      borderRadius: "4px",
                      background: "var(--surface-3)",
                      color: "var(--text-2)",
                    }}
                  >
                    {mode === "advanced"
                      ? t("analysis.tagAdvanced")
                      : mode === "external"
                        ? t("analysis.tagExternal")
                        : t("analysis.tagBasic")}
                  </span>
                  <HelpTip text={t("analysis.helpTip")} />
                </h3>
                {!analysis &&
                  !project?.autoStartAnalysis &&
                  (() => {
                    const stillProcessing = episodes.some((e) =>
                      [
                        "pending_normalization",
                        "normalizing",
                        "pending_pronoun_resolve",
                        "resolving_pronouns",
                      ].includes(e.status),
                    );
                    return stillProcessing ? (
                      <span
                        style={{ color: "var(--text-3)", fontSize: "0.85rem" }}
                      >
                        {t("analysis.waitForProcessing")}
                      </span>
                    ) : (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleStartAnalysis();
                        }}
                        disabled={retryingAnalysis}
                        style={{
                          background: "var(--primary)",
                          color: "#fff",
                          border: "none",
                          borderRadius: "4px",
                          padding: "4px 12px",
                          fontSize: "0.8rem",
                          cursor: retryingAnalysis ? "not-allowed" : "pointer",
                          opacity: retryingAnalysis ? 0.7 : 1,
                          display: "flex",
                          alignItems: "center",
                          gap: "6px",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {retryingAnalysis && (
                          <span
                            className="loading-spinner"
                            style={{
                              width: "12px",
                              height: "12px",
                              borderWidth: "2px",
                            }}
                          />
                        )}
                        {retryingAnalysis
                          ? t("analysis.startingEllipsis")
                          : t("analysis.startAnalysis")}
                      </button>
                    );
                  })()}
                {!analysis && project?.autoStartAnalysis && (
                  <span
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "0.4rem",
                      color: "var(--text-2)",
                      fontSize: "0.85rem",
                    }}
                  >
                    <span className="loading-spinner"></span>
                    {t("analysis.waitAutoStart")}
                  </span>
                )}
                {analysis && analysis.status === "pending_normalization" && (
                  <span
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "0.4rem",
                      color: "var(--text-2)",
                      fontSize: "0.85rem",
                    }}
                  >
                    <span className="loading-spinner"></span>
                    {(() => {
                      const normalizedCount = episodes.filter(
                        (e) => !!e.normalizedContentURL,
                      ).length;
                      const totalCount = episodes.length;
                      if (totalCount > 0 && normalizedCount < totalCount) {
                        return t("analysis.waitNormalization", {
                          done: normalizedCount,
                          total: totalCount,
                        });
                      }
                      if (totalCount > 0 && normalizedCount >= totalCount) {
                        return t("analysis.normalizationDone");
                      }
                      return t("analysis.waitNormalizationGeneral");
                    })()}
                  </span>
                )}
                {analysis && analysis.status === "analyzing" && (
                  <>
                    <span
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "0.4rem",
                        color: "var(--text-2)",
                        fontSize: "0.85rem",
                      }}
                    >
                      <span className="loading-spinner"></span>
                      {(() => {
                        const phase = analysis.progressPhase;
                        const total = analysis.progressTotal || 0;
                        const completed = analysis.progressCompleted || 0;
                        const pct =
                          total > 0 ? Math.round((completed / total) * 100) : 0;
                        if (phase === "extracting" && total > 0)
                          return t("analysis.analyzingProgress", { pct });
                        if (phase === "consolidating")
                          return t("analysis.consolidating");
                        if (phase === "advanced_extracting" && total > 0)
                          return t("analysis.advancedExtractingPct", { pct });
                        if (phase === "advanced_extracting")
                          return t("analysis.advancedExtractingNoPct");
                        if (phase === "advanced_merging" && total > 0)
                          return t("analysis.advancedMergingProgress", {
                            completed,
                            total,
                          });
                        if (phase === "advanced_merging")
                          return t("analysis.advancedMergingNoPct");
                        if (phase === "generating_prompts" && total > 0)
                          return t("analysis.generatingPromptsPct", { pct });
                        return t("analysis.analyzingEllipsis");
                      })()}
                    </span>
                    {analysis.isAdvanced && (
                      <button
                        onClick={async (e) => {
                          e.stopPropagation();
                          if (!id || cancellingAnalysis) return;
                          if (!window.confirm(t("analysis.cancelConfirm")))
                            return;
                          setCancellingAnalysis(true);
                          try {
                            await cancelAnalysis(id);
                            setAnalysis((prev) =>
                              prev ? { ...prev, status: "cancelling" } : prev,
                            );
                          } catch (err: any) {
                            toast.error(
                              err.message || t("analysis.cancelFailed"),
                            );
                          } finally {
                            setCancellingAnalysis(false);
                          }
                        }}
                        disabled={cancellingAnalysis}
                        style={{
                          background: "none",
                          border: "1px solid var(--danger)",
                          color: "var(--danger)",
                          borderRadius: "4px",
                          padding: "2px 8px",
                          fontSize: "0.75rem",
                          cursor: cancellingAnalysis
                            ? "not-allowed"
                            : "pointer",
                          display: "flex",
                          alignItems: "center",
                          gap: "4px",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {cancellingAnalysis && (
                          <span
                            className="loading-spinner"
                            style={{
                              width: "10px",
                              height: "10px",
                              borderWidth: "2px",
                            }}
                          ></span>
                        )}
                        {cancellingAnalysis
                          ? t("analysis.cancellingEllipsis")
                          : t("analysis.cancelAnalysis")}
                      </button>
                    )}
                  </>
                )}
                {analysis &&
                  (analysis.status === "extracted" ||
                    analysis.status === "completed") && (
                    <span
                      style={{
                        color: "var(--text-2)",
                        fontSize: "0.85rem",
                        display: "flex",
                        flexWrap: "wrap",
                        alignItems: "center",
                        gap: "0 0.5rem",
                      }}
                    >
                      <span style={{ whiteSpace: "nowrap" }}>
                        {t("analysis.summary", {
                          chars: charGroups.length,
                          scenes: sceneGroups.length,
                          props: propGroups.length,
                        })}
                      </span>
                      {analysis.status === "extracted" && (
                        <span
                          style={{
                            color: "var(--warning, #e6a23c)",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {t("analysis.pendingPrompts")}
                        </span>
                      )}
                      {analysis.updatedAt && (
                        <span
                          style={{
                            color: "var(--text-3)",
                            whiteSpace: "nowrap",
                          }}
                        >
                          · {formatDate(analysis.updatedAt)}
                        </span>
                      )}
                      {characterImageWorkflow === "casting_styling" && (
                        <>
                          <button
                            className="btn btn-secondary"
                            style={{
                              fontSize: "0.8rem",
                              padding: "0.3rem 0.75rem",
                              marginLeft: "0.5rem",
                              background: "var(--theme-scene)",
                              color: "#fff",
                              border: "none",
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              setAssetShareForm({
                                label: "",
                                hours: 72,
                                maxViews: 0,
                              });
                              setAssetShareModal(true);
                            }}
                          >
                            {t("analysis.shareAssets")}
                          </button>
                          <button
                            className="btn btn-secondary"
                            style={{
                              fontSize: "0.8rem",
                              padding: "0.3rem 0.75rem",
                              marginLeft: "0.5rem",
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              openShareHistory();
                            }}
                          >
                            {t("analysis.shareHistory")}
                          </button>
                        </>
                      )}
                    </span>
                  )}
                {analysis && analysis.status === "generating_prompts" && (
                  <span
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "0.4rem",
                      color: "var(--text-2)",
                      fontSize: "0.85rem",
                    }}
                  >
                    <span className="loading-spinner"></span>
                    {t("analysis.generatingPromptsStatus")}
                  </span>
                )}
                {/* 收起状态下显示搜索进度小标记(已移入搜索状态条) */}
                {!analysisExpanded && searchIsActive && (
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "4px",
                      color:
                        isGeneratingKeywords && placeholderTimedOut
                          ? "var(--warning, #e6a23c)"
                          : "var(--text-3)",
                      fontSize: "0.75rem",
                    }}
                  >
                    {!(isGeneratingKeywords && placeholderTimedOut) && (
                      <span
                        className="loading-spinner"
                        style={{
                          width: "10px",
                          height: "10px",
                          borderWidth: "2px",
                        }}
                      ></span>
                    )}
                    {isGeneratingKeywords
                      ? placeholderTimedOut
                        ? t("analysis.keywordTimeoutShort")
                        : t("analysis.keywordGeneratingShort")
                      : `${searchActiveCount > 0 ? t("analysis.searchInProgressCount", { count: searchActiveCount }) : ""}${searchQueuedCount > 0 ? `${searchActiveCount > 0 ? " · " : ""}${t("analysis.queuedCount", { count: searchQueuedCount })}` : ""}`}
                  </span>
                )}

                {analysis && analysis.status === "cancelling" && (
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "6px",
                      color: "var(--warning, #e6a23c)",
                      fontSize: "0.85rem",
                    }}
                  >
                    <span
                      className="loading-spinner"
                      style={{
                        width: "12px",
                        height: "12px",
                        borderWidth: "2px",
                      }}
                    />
                    {t("analysis.cancellingStatus")}
                  </span>
                )}
                {analysis && analysis.status === "cancelled" && (
                  <>
                    <span
                      style={{
                        color: "var(--warning, #e6a23c)",
                        fontSize: "0.85rem",
                      }}
                    >
                      {t("analysis.cancelledStatus")}
                    </span>
                    <button
                      onClick={async (e) => {
                        e.stopPropagation();
                        if (!id || retryingAnalysis) return;
                        setRetryingAnalysis(true);
                        try {
                          await retryAnalysis(id);
                          setAnalysis((prev) =>
                            prev ? { ...prev, status: "analyzing" } : prev,
                          );
                        } catch (err: any) {
                          toast.error(
                            err.message ||
                              t("confirmDialogs.reanalyze.reanalysisFailed"),
                          );
                        } finally {
                          setRetryingAnalysis(false);
                        }
                      }}
                      disabled={retryingAnalysis}
                      style={{
                        background: "none",
                        border: "1px solid var(--primary)",
                        color: "var(--primary)",
                        borderRadius: "4px",
                        padding: "2px 8px",
                        fontSize: "0.75rem",
                        cursor: retryingAnalysis ? "not-allowed" : "pointer",
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                      }}
                    >
                      {retryingAnalysis && (
                        <span
                          className="loading-spinner"
                          style={{
                            width: "10px",
                            height: "10px",
                            borderWidth: "2px",
                          }}
                        ></span>
                      )}
                      {retryingAnalysis
                        ? t("analysis.startingEllipsis")
                        : t("analysis.reanalyze")}
                    </button>
                  </>
                )}
                {analysis && analysis.status === "failed" && (
                  <>
                    <span
                      style={{ color: "var(--danger)", fontSize: "0.85rem" }}
                    >
                      {t("analysis.analysisFailed")}
                    </span>
                    {analysis.errorMsg && (
                      <span
                        style={{
                          color: "var(--text-3)",
                          fontSize: "0.72rem",
                          maxWidth: "300px",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                        title={analysis.errorMsg}
                      >
                        {analysis.errorMsg}
                      </span>
                    )}
                    <button
                      onClick={async (e) => {
                        e.stopPropagation();
                        if (!id || retryingAnalysis) return;
                        setRetryingAnalysis(true);
                        try {
                          await retryAnalysis(id);
                          setAnalysis((prev) =>
                            prev ? { ...prev, status: "analyzing" } : prev,
                          );
                        } catch (err: any) {
                          toast.error(
                            err.message ||
                              t("confirmDialogs.reanalyze.reanalysisFailed"),
                          );
                        } finally {
                          setRetryingAnalysis(false);
                        }
                      }}
                      disabled={retryingAnalysis}
                      style={{
                        background: "none",
                        border: `1px solid ${analysis.isAdvanced ? "var(--primary)" : "var(--danger)"}`,
                        color: analysis.isAdvanced
                          ? "var(--primary)"
                          : "var(--danger)",
                        borderRadius: "4px",
                        padding: "2px 8px",
                        fontSize: "0.75rem",
                        cursor: retryingAnalysis ? "not-allowed" : "pointer",
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                      }}
                    >
                      {retryingAnalysis && (
                        <span
                          className="loading-spinner"
                          style={{
                            width: "10px",
                            height: "10px",
                            borderWidth: "2px",
                          }}
                        ></span>
                      )}
                      {retryingAnalysis
                        ? t("analysis.startingEllipsis")
                        : analysis.isAdvanced
                          ? t("analysis.continueAnalysis")
                          : t("analysis.reanalyze")}
                    </button>
                  </>
                )}
              </div>
              {/* 参考图搜索状态 - inline with title */}
              {id &&
                analysis &&
                (analysis.status === "completed" ||
                  analysis.status === "extracted") && (
                  <div
                    onClick={(e) => e.stopPropagation()}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                      flexShrink: 0,
                      marginLeft: "auto",
                    }}
                  >
                    <span
                      style={{
                        fontSize: "0.85rem",
                        color: "var(--text-3)",
                        whiteSpace: "nowrap",
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "4px",
                      }}
                    >
                      {/* 搜索进行中 (关键词生成或搜图) */}
                      {(isGeneratingKeywords ||
                        (!isGeneratingKeywords && searchingCount > 0)) && (
                        <>
                          <span
                            className="loading-spinner"
                            style={{
                              width: "10px",
                              height: "10px",
                              borderWidth: "2px",
                            }}
                          ></span>
                          <span>{t("analysis.refMatchingInProgress")}</span>
                        </>
                      )}
                      {/* 已完成 */}
                      {!searchIsActive && searchSummary && (
                        <span style={{ color: "var(--success, #67c23a)" }}>
                          {t("analysis.refMatchedCount", {
                            completed: searchTasks.filter(
                              (task) => task.status === "completed",
                            ).length,
                            total: searchTasks.length,
                          })}
                        </span>
                      )}
                      {/* 从未搜索 */}
                      {!searchIsActive && !searchSummary && (
                        <span>{t("analysis.refUnmatched")}</span>
                      )}
                    </span>
                    {/* 操作按钮 */}
                    {!searchIsActive || placeholderTimedOut ? (
                      <span
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "6px",
                        }}
                      >
                        <button
                          onClick={() => setConfirmResearchOpen(true)}
                          disabled={triggeringSearch}
                          style={{
                            background: "none",
                            border: "1px solid var(--theme-scene)",
                            color: "var(--theme-scene)",
                            borderRadius: "4px",
                            padding: "2px 10px",
                            fontSize: "0.85rem",
                            cursor: triggeringSearch
                              ? "not-allowed"
                              : "pointer",
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "4px",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {triggeringSearch && (
                            <span
                              className="loading-spinner"
                              style={{
                                width: "10px",
                                height: "10px",
                                borderWidth: "2px",
                              }}
                            ></span>
                          )}
                          {searchSummary
                            ? t("analysis.regenerateKeywords")
                            : t("analysis.generateKeywords")}
                        </button>
                        {/* 按分类重新生成关键词 */}
                        {searchTasks.some((t) => t.category !== 0) &&
                          !triggeringSearch && (
                            <>
                              {(
                                [
                                  { cat: 1, label: t("analysis.character") },
                                  { cat: 2, label: t("analysis.scene") },
                                  { cat: 3, label: t("analysis.prop") },
                                ] as const
                              ).map((item) => (
                                <button
                                  key={item.cat}
                                  onClick={() =>
                                    setConfirmCategorySearch({
                                      cat: item.cat,
                                      label: item.label,
                                    })
                                  }
                                  style={{
                                    background: "none",
                                    border: "1px solid var(--border)",
                                    color: "var(--text-2)",
                                    borderRadius: "4px",
                                    padding: "2px 8px",
                                    fontSize: "0.85rem",
                                    cursor: "pointer",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {item.label}
                                </button>
                              ))}
                            </>
                          )}
                      </span>
                    ) : null}
                  </div>
                )}
            </div>
            {/* 搜索详情折叠面板 - outside header div */}
            {id &&
              analysis &&
              (analysis.status === "completed" ||
                analysis.status === "extracted") &&
              searchDetailExpanded &&
              activeSearchDetails.length > 0 && (
                <div
                  style={{
                    margin: "4px 0 0",
                    padding: "6px 10px",
                    background: "var(--bg-1, #fff)",
                    border: "1px solid var(--border)",
                    borderRadius: "4px",
                    fontSize: "0.72rem",
                    color: "var(--text-2)",
                    maxHeight: "160px",
                    overflowY: "auto",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: "4px 10px",
                    }}
                  >
                    {activeSearchDetails.map((item, i) => {
                      const tabMap: Record<
                        number,
                        "character" | "scene" | "prop"
                      > = { 1: "character", 2: "scene", 3: "prop" };
                      const tab = tabMap[item.category];
                      const elemId = tab ? `elem-${tab}-${item.assetName}` : "";
                      return (
                        <span
                          key={i}
                          onClick={() => {
                            if (!tab) return;
                            setAnalysisTab(tab);
                            requestAnimationFrame(() => {
                              const el = document.getElementById(elemId);
                              if (el) {
                                el.scrollIntoView({
                                  behavior: "smooth",
                                  block: "center",
                                });
                                el.style.outline = "2px solid var(--primary)";
                                el.style.outlineOffset = "2px";
                                setTimeout(() => {
                                  el.style.outline = "";
                                  el.style.outlineOffset = "";
                                }, 2000);
                              }
                            });
                          }}
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "3px",
                            whiteSpace: "nowrap",
                            cursor: "pointer",
                            borderRadius: "3px",
                            padding: "1px 4px",
                          }}
                          onMouseEnter={(e) => {
                            (e.currentTarget as HTMLElement).style.background =
                              "var(--bg-2, #f0f0f0)";
                          }}
                          onMouseLeave={(e) => {
                            (e.currentTarget as HTMLElement).style.background =
                              "";
                          }}
                          title={t("analysis.jumpTo", {
                            category: item.categoryName,
                            name: item.assetName,
                          })}
                        >
                          {item.status === "searching" ? (
                            <span
                              style={{
                                display: "inline-block",
                                width: "7px",
                                height: "7px",
                                borderRadius: "50%",
                                background: "var(--warning, #e6a23c)",
                                flexShrink: 0,
                              }}
                            />
                          ) : (
                            <span
                              style={{
                                display: "inline-block",
                                width: "7px",
                                height: "7px",
                                borderRadius: "50%",
                                border: "1.5px solid var(--text-4, #ccc)",
                                flexShrink: 0,
                              }}
                            />
                          )}
                          <span
                            style={{
                              color: "var(--text-3)",
                              fontSize: "0.65rem",
                            }}
                          >
                            [{item.categoryName}]
                          </span>
                          {item.assetName}
                          {item.assetState ? `(${item.assetState})` : ""}
                          {item.count > 1 && (
                            <span style={{ color: "var(--text-4)" }}>
                              ×{item.count}
                            </span>
                          )}
                        </span>
                      );
                    })}
                  </div>
                </div>
              )}

            <div
              style={{
                maxHeight: analysisExpanded ? "none" : "0",
                opacity: analysisExpanded ? 1 : 0,
                overflow: analysisExpanded ? "visible" : "hidden",
                transition: "opacity 0.25s ease",
              }}
            >
              {analysis &&
                (analysis.status === "completed" ||
                  analysis.status === "extracted") &&
                id && (
                  <div style={{ marginTop: "1.25rem" }}>
                    {/* 按类别手动触发搜索按钮 */}
                    {searchTasks.length > 0 &&
                      !isGeneratingKeywords &&
                      (() => {
                        const pendingByCategory = (cat: number) =>
                          searchTasks.filter(
                            (t) => t.category === cat && t.status === "pending",
                          ).length;
                        const searchingByCategory = (cat: number) =>
                          searchTasks.filter(
                            (t) =>
                              t.category === cat &&
                              (t.status === "queued" ||
                                t.status === "searching"),
                          ).length;
                        const scenePending = pendingByCategory(2);
                        const propPending = pendingByCategory(3);
                        const sceneSearching = searchingByCategory(2);
                        const propSearching = searchingByCategory(3);
                        const hasAnyPending =
                          scenePending > 0 || propPending > 0;
                        const hasAnySearching =
                          sceneSearching > 0 || propSearching > 0;
                        if (!hasAnyPending && !hasAnySearching) return null;
                        const btnStyle = (
                          pending: number,
                          searching: number,
                        ): React.CSSProperties => ({
                          background: "none",
                          border: `1px solid ${pending > 0 ? "var(--primary)" : "var(--border)"}`,
                          color:
                            pending > 0 ? "var(--primary)" : "var(--text-3)",
                          borderRadius: "4px",
                          padding: "2px 8px",
                          fontSize: "0.72rem",
                          cursor: pending > 0 ? "pointer" : "default",
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "4px",
                          whiteSpace: "nowrap",
                          opacity: pending > 0 ? 1 : 0.6,
                        });
                        return (
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "0.5rem",
                              marginBottom: "0.5rem",
                              fontSize: "0.75rem",
                            }}
                          >
                            <span
                              style={{
                                color: "var(--text-3)",
                                fontSize: "0.72rem",
                              }}
                            >
                              {t("analysis.manualSearch")}
                            </span>
                            {[
                              {
                                cat: 2,
                                label: t("analysis.scene"),
                                pending: scenePending,
                                searching: sceneSearching,
                              },
                              {
                                cat: 3,
                                label: t("analysis.prop"),
                                pending: propPending,
                                searching: propSearching,
                              },
                            ].map((item) => (
                              <button
                                key={item.cat}
                                onClick={() =>
                                  item.pending > 0 &&
                                  setConfirmBatchSearch({
                                    category: item.cat,
                                    label: item.label,
                                    count: item.pending,
                                  })
                                }
                                disabled={item.pending === 0}
                                style={btnStyle(item.pending, item.searching)}
                                title={item.label}
                              >
                                {item.searching > 0 && (
                                  <span
                                    style={{
                                      display: "inline-block",
                                      width: "8px",
                                      height: "8px",
                                      border: "1.5px solid currentColor",
                                      borderTopColor: "transparent",
                                      borderRadius: "50%",
                                      animation: "spin 0.8s linear infinite",
                                    }}
                                  />
                                )}
                                {item.label}
                                {item.pending > 0
                                  ? ` (${item.pending})`
                                  : item.searching > 0
                                    ? ` ${t("analysis.searchingCount", { count: item.searching })}`
                                    : " ✓"}
                              </button>
                            ))}
                          </div>
                        );
                      })()}
                    {/* Tab bar + episode filter (desktop only, mobile uses MobileAssetList tabs) */}
                    <div
                      style={{
                        display: isMobile ? "none" : "flex",
                        alignItems: "center",
                        borderBottom: `3px solid ${analysisTab === "character" ? "var(--theme-character)" : analysisTab === "scene" ? "var(--theme-scene)" : "var(--theme-prop)"}`,
                        marginBottom: "1rem",
                      }}
                    >
                      {(
                        [
                          {
                            key: "character",
                            label: t("analysis.character"),
                            count: filteredCharGroups.length,
                            themeColor: "var(--theme-character)",
                          },
                          {
                            key: "scene",
                            label: t("analysis.scene"),
                            count: filteredSceneGroups.length,
                            themeColor: "var(--theme-scene)",
                          },
                          {
                            key: "prop",
                            label: t("analysis.prop"),
                            count: filteredPropGroups.length,
                            themeColor: "var(--theme-prop)",
                          },
                        ] as const
                      ).map((tab) => (
                        <button
                          key={tab.key}
                          onClick={() => setAnalysisTab(tab.key)}
                          style={{
                            padding: "8px 20px",
                            border: "1px solid transparent",
                            borderBottom: "none",
                            background:
                              analysisTab === tab.key
                                ? tab.themeColor
                                : "transparent",
                            cursor: "pointer",
                            fontSize: "0.9rem",
                            fontWeight: analysisTab === tab.key ? 600 : 400,
                            color:
                              analysisTab === tab.key
                                ? "#fff"
                                : "var(--text-3)",
                            borderRadius: "8px 8px 0 0",
                            borderColor:
                              analysisTab === tab.key
                                ? tab.themeColor
                                : "transparent",
                            marginBottom: "-3px",
                            transition: "all 0.15s",
                            position: "relative" as const,
                          }}
                        >
                          {tab.label}
                          {tab.count > 0 ? ` (${tab.count})` : ""}
                        </button>
                      ))}
                      {allAssetEpisodes.length > 1 && (
                        <div
                          style={{
                            marginLeft: "auto",
                            display: "flex",
                            alignItems: "center",
                            gap: "0.4rem",
                            marginBottom: "-1px",
                          }}
                        >
                          <AssetSortMenu
                            value={assetSortMode}
                            onChange={setAssetSortMode}
                          />
                          <span
                            style={{
                              fontSize: "0.78rem",
                              color: "var(--text-3)",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {t("analysis.filterEpisode")}
                          </span>
                          <div
                            ref={episodeFilterRef}
                            style={{ position: "relative" }}
                          >
                            <button
                              type="button"
                              ref={episodeFilterTriggerRef}
                              onClick={() => {
                                if (!episodeFilterOpen)
                                  recomputeEpisodeFilterPos();
                                setEpisodeFilterOpen(!episodeFilterOpen);
                              }}
                              style={{
                                fontSize: "0.78rem",
                                padding: "2px 8px",
                                borderRadius: "4px",
                                border: "1px solid var(--border)",
                                background: "var(--surface)",
                                color: "var(--text)",
                                cursor: "pointer",
                                whiteSpace: "nowrap",
                                display: "flex",
                                alignItems: "center",
                                gap: "4px",
                              }}
                            >
                              {episodeFilterLabel}
                              <span style={{ fontSize: "0.6rem" }}>▼</span>
                            </button>
                            {episodeFilterOpen &&
                              episodeFilterPos &&
                              createPortal(
                                <div
                                  ref={episodeFilterMenuRef}
                                  style={{
                                    position: "fixed",
                                    top: episodeFilterPos.top,
                                    right: episodeFilterPos.right,
                                    zIndex: 1000,
                                    background: "var(--surface)",
                                    border: "1px solid var(--border)",
                                    borderRadius: "8px",
                                    boxShadow: "0 4px 16px rgba(0,0,0,0.15)",
                                    padding: "0.6rem",
                                    width: "320px",
                                    maxHeight: episodeFilterPos.maxHeight,
                                    overflowY: "auto",
                                  }}
                                >
                                <div
                                  style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    marginBottom: "0.5rem",
                                  }}
                                >
                                  <span
                                    style={{
                                      fontSize: "0.75rem",
                                      color: "var(--text-3)",
                                    }}
                                  >
                                    {t("analysis.filterEpisode")}
                                  </span>
                                  <button
                                    type="button"
                                    onClick={() => setFilterEpisodes([])}
                                    disabled={filterEpisodes.length === 0}
                                    style={{
                                      fontSize: "0.75rem",
                                      padding: "1px 8px",
                                      borderRadius: "4px",
                                      border: "1px solid var(--border)",
                                      background: "transparent",
                                      color:
                                        filterEpisodes.length === 0
                                          ? "var(--text-3)"
                                          : "var(--primary)",
                                      cursor:
                                        filterEpisodes.length === 0
                                          ? "default"
                                          : "pointer",
                                    }}
                                  >
                                    {t("analysis.allEpisodes")}
                                  </button>
                                </div>
                                <div
                                  style={{
                                    display: "grid",
                                    gridTemplateColumns: "repeat(4, 1fr)",
                                    gap: "6px",
                                  }}
                                >
                                  {allAssetEpisodes.map((ep) => {
                                    const isSelected =
                                      filterEpisodes.includes(ep);
                                    return (
                                      <button
                                        key={ep}
                                        type="button"
                                        onClick={() => toggleFilterEpisode(ep)}
                                        style={{
                                          fontSize: "0.78rem",
                                          padding: "4px 0",
                                          borderRadius: "4px",
                                          border: isSelected
                                            ? "1px solid var(--primary)"
                                            : "1px solid var(--border)",
                                          background: isSelected
                                            ? "var(--primary-light)"
                                            : "transparent",
                                          color: isSelected
                                            ? "var(--primary)"
                                            : "var(--text)",
                                          fontWeight: isSelected ? 600 : 400,
                                          cursor: "pointer",
                                          whiteSpace: "nowrap",
                                        }}
                                      >
                                        {t("analysis.episodeN", { n: ep })}
                                      </button>
                                    );
                                  })}
                                </div>
                                </div>,
                                document.body,
                              )}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Tab content: sidebar nav + content */}
                    {(() => {
                      // 解析禁用资产列表
                      const disabledAssets: Array<{
                        category: number;
                        name: string;
                      }> = project?.disabledAssets
                        ? tryParseJSON(project.disabledAssets)
                        : [];
                      const disabledSet = new Set(
                        Array.isArray(disabledAssets)
                          ? disabledAssets.map((d) => `${d.category}:${d.name}`)
                          : [],
                      );
                      const currentCategory =
                        analysisTab === "character"
                          ? 1
                          : analysisTab === "scene"
                            ? 2
                            : 3;
                      const isAssetDisabled = (name: string) =>
                        disabledSet.has(`${currentCategory}:${name}`);

                      // 计算每个资产组的图片状态：green=有状态图, yellow=仅有基础图, red=都没有
                      const getGroupStatus = (
                        states: Array<{
                          selectedImageUrl?: string;
                        }>,
                        baseMetadata: {
                          selectedBaseImageUrl: string;
                          conflictFields: string[];
                        },
                      ): "red" | "yellow" | "green" => {
                        const hasStateImage = states.some(
                          (s) => !!s.selectedImageUrl,
                        );
                        if (hasStateImage) return "green";
                        if (
                          baseMetadata.selectedBaseImageUrl &&
                          !baseMetadata.conflictFields.includes("selectedBaseImageUrl")
                        )
                          return "yellow";
                        return "red";
                      };
                      const navStatusColors = {
                        red: {
                          bg: "rgba(220, 38, 38, 0.08)",
                          hoverBg: "rgba(220, 38, 38, 0.14)",
                          color: "var(--danger)",
                        },
                        yellow: {
                          bg: "rgba(221, 91, 0, 0.08)",
                          hoverBg: "rgba(221, 91, 0, 0.14)",
                          color: "var(--warning)",
                        },
                        green: {
                          bg: "rgba(20, 184, 166, 0.08)",
                          hoverBg: "rgba(20, 184, 166, 0.14)",
                          color: "#0d9488",
                        },
                        disabled: {
                          bg: "var(--surface-2)",
                          hoverBg: "var(--border)",
                          color: "var(--text-3)",
                        },
                      };

                      const analysisActionButtons =
                        (analysis.status === "completed" ||
                          analysis.status === "extracted") &&
                        id ? (
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "0.5rem",
                              marginLeft: "auto",
                              flexShrink: 0,
                            }}
                          >
                            <button
                              onClick={() => setConfirmGenPromptsOpen(true)}
                              style={{
                                background:
                                  analysis.status === "extracted"
                                    ? "var(--primary)"
                                    : "none",
                                color:
                                  analysis.status === "extracted"
                                    ? "#fff"
                                    : "var(--primary)",
                                border: "1px solid var(--primary)",
                                borderRadius: "4px",
                                padding: "2px 8px",
                                fontSize: "0.75rem",
                                cursor: "pointer",
                                whiteSpace: "nowrap",
                                fontWeight:
                                  analysis.status === "extracted" ? 600 : 400,
                              }}
                            >
                              {analysis.status === "extracted"
                                ? t("analysis.generateImagePrompts")
                                : t("analysis.regenerateImagePrompts")}
                            </button>
                            {hasPermission(currentUser, 'operations') && (
                              <button
                                onClick={() => {
                                  if (retryingAnalysis) return;
                                  setConfirmReanalyzeStep(1);
                                  setConfirmReanalyzeOpen(true);
                                }}
                                disabled={retryingAnalysis}
                                style={{
                                  background: "none",
                                  border: "1px solid var(--danger)",
                                  color: "var(--danger)",
                                  borderRadius: "4px",
                                  padding: "2px 8px",
                                  fontSize: "0.75rem",
                                  cursor: retryingAnalysis
                                    ? "not-allowed"
                                    : "pointer",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {retryingAnalysis && (
                                  <span
                                    className="loading-spinner"
                                    style={{
                                      width: "10px",
                                      height: "10px",
                                      borderWidth: "2px",
                                    }}
                                  ></span>
                                )}
                                {retryingAnalysis
                                  ? t("analysis.analyzingEllipsis")
                                  : t("analysis.reanalyze")}
                              </button>
                            )}
                            {analysis.assetJsonUrl && (
                              <button
                                onClick={() => {
                                  fetch(analysis.assetJsonUrl!)
                                    .then((r) => r.blob())
                                    .then((blob) => {
                                      const a = document.createElement("a");
                                      a.href = URL.createObjectURL(blob);
                                      a.download = "assets.json";
                                      a.click();
                                      URL.revokeObjectURL(a.href);
                                    })
                                    .catch(() =>
                                      window.open(
                                        analysis.assetJsonUrl!,
                                        "_blank",
                                      ),
                                    );
                                }}
                                style={{
                                  background: "none",
                                  border: "1px solid var(--primary)",
                                  color: "var(--primary)",
                                  borderRadius: "4px",
                                  padding: "2px 8px",
                                  fontSize: "0.75rem",
                                  cursor: "pointer",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {t("analysis.downloadAssetJSON")}
                              </button>
                            )}
                          </div>
                        ) : null;

                      const groupHasNew = (
                        group: {
                          name: string;
                          baseMetadata: { baseImageUrls: string[] };
                          states: Array<{ state?: string; imageUrls?: string[] }>;
                        },
                      ) => {
                        const pfx = `thumb-seen:${id}:${group.name}`;
                        const bs = localStorage.getItem(`${pfx}:base`);
                        if (
                          bs !== null &&
                          group.baseMetadata.baseImageUrls.length > parseInt(bs, 10)
                        )
                          return true;
                        return group.states.some((s) => {
                          const sv = localStorage.getItem(
                            `${pfx}:${s.state || "default"}`,
                          );
                          if (sv === null) return false;
                          return (s.imageUrls?.length || 0) > parseInt(sv, 10);
                        });
                      };

                      const navItems: {
                        name: string;
                        id: string;
                        status: "red" | "yellow" | "green";
                        disabled: boolean;
                        hasNew: boolean;
                      }[] =
                        analysisTab === "character"
                          ? filteredCharGroups.map((g) => ({
                              name: g.name,
                              id: `elem-character-${g.name}`,
                              status: getGroupStatus(g.states, g.baseMetadata),
                              disabled: isAssetDisabled(g.name),
                              hasNew: groupHasNew(g),
                            }))
                          : analysisTab === "scene"
                            ? filteredSceneGroups.map((g) => ({
                                name: g.name,
                                id: `elem-scene-${g.name}`,
                                status: getGroupStatus(g.states, g.baseMetadata),
                                disabled: isAssetDisabled(g.name),
                                hasNew: groupHasNew(g),
                              }))
                            : filteredPropGroups.map((g) => ({
                                name: g.name,
                                id: `elem-prop-${g.name}`,
                                status: getGroupStatus(g.states, g.baseMetadata),
                                disabled: isAssetDisabled(g.name),
                                hasNew: groupHasNew(g),
                              }));

                      // 禁用/启用资产处理
                      const handleToggleDisable = async (
                        name: string,
                        isCurrentlyDisabled: boolean,
                      ) => {
                        if (!project) return;
                        if (isCurrentlyDisabled) {
                          // 启用：直接调用
                          try {
                            await enableAsset(
                              project.id,
                              currentCategory,
                              name,
                            );
                            // 更新本地 project 状态
                            const newDisabled = disabledAssets.filter(
                              (d) =>
                                !(
                                  d.category === currentCategory &&
                                  d.name === name
                                ),
                            );
                            setProject({
                              ...project,
                              disabledAssets: JSON.stringify(newDisabled),
                            });
                            toast.success(
                              t("confirmDialogs.disableAsset.enabledSuccess", {
                                name,
                              }),
                            );
                          } catch (e: any) {
                            toast.error(
                              e.message ||
                                t("confirmDialogs.disableAsset.enableFailed"),
                            );
                          }
                          return;
                        }
                        // 禁用：先预检查
                        setDisableAssetLoading(true);
                        try {
                          const result = await disableAsset(
                            project.id,
                            currentCategory,
                            name,
                            false,
                          );
                          if (result.needConfirmation) {
                            setDisableAssetPending({
                              category: currentCategory,
                              name,
                            });
                            setDisableAssetAffectedCount(
                              result.affectedShotCount || 0,
                            );
                            setDisableAssetAffectedDetails(
                              result.affectedDetails || [],
                            );
                            setDisableAssetStep(1);
                          } else {
                            // 无分镜引用，直接禁用
                            await disableAsset(
                              project.id,
                              currentCategory,
                              name,
                              true,
                            );
                            const newDisabled = [
                              ...disabledAssets,
                              { category: currentCategory, name },
                            ];
                            setProject({
                              ...project,
                              disabledAssets: JSON.stringify(newDisabled),
                            });
                            toast.success(
                              t("confirmDialogs.disableAsset.disabledNoRef", {
                                name,
                              }),
                            );
                          }
                        } catch (e: any) {
                          toast.error(
                            e.message ||
                              t("confirmDialogs.disableAsset.operationFailed"),
                          );
                        } finally {
                          setDisableAssetLoading(false);
                        }
                      };

                      const handleNavClick = (elemId: string) => {
                        const el = document.getElementById(elemId);
                        if (el) {
                          const headerH =
                            parseFloat(
                              getComputedStyle(
                                document.documentElement,
                              ).getPropertyValue("--header-height"),
                            ) || 58;
                          const top =
                            el.getBoundingClientRect().top +
                            window.scrollY -
                            headerH -
                            12;
                          window.scrollTo({ top, behavior: "smooth" });
                          el.style.outline = "2px solid var(--primary)";
                          setTimeout(() => {
                            el.style.outline = "";
                          }, 1500);
                        }
                      };

                      // 资产重命名：category 从当前 analysisTab 推断；重命名始终作用于该资产的所有状态
                      const handleRename = (assetName: string) => {
                        setRenameTarget({
                          category: analysisTab,
                          name: assetName,
                        });
                      };

                      /* ── Mobile: list + drawer ── */
                      if (isMobile) {
                        // Find the selected group for the drawer
                        const drawerGroup = mobileDrawerAsset
                          ? mobileDrawerAsset.type === "character"
                            ? filteredCharGroups.find(
                                (g) => g.name === mobileDrawerAsset.name,
                              )
                            : mobileDrawerAsset.type === "scene"
                              ? filteredSceneGroups.find(
                                  (g) => g.name === mobileDrawerAsset.name,
                                )
                              : filteredPropGroups.find(
                                  (g) => g.name === mobileDrawerAsset.name,
                                )
                          : null;

                        return (
                          <>
                            <MobileAssetList
                              tab={analysisTab}
                              onTabChange={setAnalysisTab}
                              charGroups={filteredCharGroups}
                              sceneGroups={filteredSceneGroups}
                              propGroups={filteredPropGroups}
                              onSelectAsset={(type, name) =>
                                setMobileDrawerAsset({ type, name })
                              }
                              isDisabled={isAssetDisabled}
                              charCount={filteredCharGroups.length}
                              sceneCount={filteredSceneGroups.length}
                              propCount={filteredPropGroups.length}
                            />

                            <MobileAssetDrawer
                              open={!!mobileDrawerAsset && !!drawerGroup}
                              title={mobileDrawerAsset?.name || ""}
                              onClose={() => setMobileDrawerAsset(null)}
                            >
                              {mobileDrawerAsset &&
                                drawerGroup &&
                                mobileDrawerAsset.type === "character" && (
                                  <CharacterGroup
                                    projectId={id}
                                    name={drawerGroup.name}
                                    states={
                                      (
                                        drawerGroup as (typeof filteredCharGroups)[0]
                                      ).states
                                    }
                                    baseMetadata={drawerGroup.baseMetadata}
                                    analysisAuthorityRevision={analysisAuthorityRevision}
                                    voiceUrl={characterVoiceMap.get(drawerGroup.name)?.voiceUrl}
                                    basicState={
                                      (
                                        drawerGroup as (typeof filteredCharGroups)[0]
                                      ).basicState
                                    }
                                    onUpdate={handleElementUpdate}
                                    onAddState={handleCharAddState}
                                    activeTaskMap={activeTaskMap}
                                    failedTaskMap={failedTaskMap}
                                    baseActiveTaskMap={baseActiveTaskMap}
                                    baseFailedTaskMap={baseFailedTaskMap}
                                    onTaskResolved={onImageTaskResolved}
                                    fixedPrompt={
                                      project?.charFixedPrompt ||
                                      DEFAULT_FIXED_IMAGE_PROMPT
                                    }
                                    projectRefImages={parseRefImages(
                                      project?.charProjectRefImages,
                                    )}
                                    characterImageWorkflow={
                                      effectiveCharacterImageWorkflow
                                    }
                                    imagePlatform={project?.imagePlatform}
                                    imageModel={project?.imageModel}
                                    imageModels={activeImageModels}
                                    episodeMap={episodeMap}
                                    charStatePromptPrefix={
                                      project?.charStatePromptPrefix
                                    }
                                    onBaseGenerated={() =>
                                      id && loadAnalysis(id)
                                    }
                                    onGenerateComplete={() =>
                                      id && loadAnalysis(id)
                                    }
                                    onSearchSelect={openSearchSelectModal}
                                    getSearchImageCount={getSearchImageCount}
                                    onSearchSelectAllStates={
                                      openSearchSelectModalAllStates
                                    }
                                    getSearchImageCountAllStates={
                                      getSearchImageCountAllStates
                                    }
                                    getSearchTaskProgress={
                                      getSearchTaskProgress
                                    }
                                    getSearchTaskProgressAllStates={
                                      getSearchTaskProgressAllStates
                                    }
                                    onNavigateToProp={(propName) => {
                                      setMobileDrawerAsset(null);
                                      setTimeout(() => {
                                        setAnalysisTab("prop");
                                        setTimeout(
                                          () =>
                                            setMobileDrawerAsset({
                                              type: "prop",
                                              name: propName,
                                            }),
                                          100,
                                        );
                                      }, 350);
                                    }}
                                    allProps={props}
                                    isDisabled={isAssetDisabled(
                                      drawerGroup.name,
                                    )}
                                    onToggleDisable={handleToggleDisable}
                                    disableLoading={disableAssetLoading}
                                    onRename={handleRename}
                                    onComment={(name) => setCommentTarget({ category: 'character', name })}
                                    pendingCommentCount={pendingCommentCountMap?.get(`character|${drawerGroup.name}`) ?? 0}
                                    reviewStatusMap={
                                      isRealPerson ? reviewStatusMap : undefined
                                    }
                                    onReviewInvalidated={
                                      isRealPerson
                                        ? loadProjectReviews
                                        : undefined
                                    }
                                    availableEpisodes={availableEpisodeOptions}
                                    onEpisodesUpdate={(rawName, state, eps) =>
                                      handleElementEpisodesUpdate(
                                        "character",
                                        rawName,
                                        state,
                                        eps,
                                      )
                                    }
                                  />
                                )}
                              {mobileDrawerAsset &&
                                drawerGroup &&
                                mobileDrawerAsset.type === "scene" && (
                                  <ScenePropGroup
                                    projectId={id}
                                    elementType="scene"
                                    imagePlatform={project?.imagePlatform}
                                    imageModel={project?.imageModel}
                                    baseImagePlatform={
                                      project?.baseImagePlatform
                                    }
                                    baseImageModel={project?.baseImageModel}
                                    imageModels={activeImageModels}
                                    name={drawerGroup.name}
                                    states={
                                      (
                                        drawerGroup as (typeof filteredSceneGroups)[0]
                                      ).states
                                    }
                                    baseMetadata={drawerGroup.baseMetadata}
                                    analysisAuthorityRevision={analysisAuthorityRevision}
                                    onUpdate={handleElementUpdate}
                                    onAddState={handleSceneAddState}
                                    activeTaskMap={activeTaskMap}
                                    failedTaskMap={failedTaskMap}
                                    baseActiveTaskMap={baseActiveTaskMap}
                                    baseFailedTaskMap={baseFailedTaskMap}
                                    onTaskResolved={onImageTaskResolved}
                                    fixedPrompt={
                                      project?.sceneFixedPrompt ||
                                      DEFAULT_FIXED_IMAGE_PROMPT
                                    }
                                    projectRefImages={parseRefImages(
                                      project?.sceneProjectRefImages,
                                    )}
                                    episodeMap={episodeMap}
                                    onBaseGenerated={() =>
                                      id && loadAnalysis(id)
                                    }
                                    onGenerateComplete={() =>
                                      id && loadAnalysis(id)
                                    }
                                    statePromptPrefix={
                                      project?.sceneStatePromptPrefix
                                    }
                                    onSearchSelect={openSearchSelectModal}
                                    getSearchImageCount={getSearchImageCount}
                                    onSearchSelectAllStates={
                                      openSearchSelectModalAllStates
                                    }
                                    getSearchImageCountAllStates={
                                      getSearchImageCountAllStates
                                    }
                                    getSearchTaskProgress={
                                      getSearchTaskProgress
                                    }
                                    getSearchTaskProgressAllStates={
                                      getSearchTaskProgressAllStates
                                    }
                                    isDisabled={isAssetDisabled(
                                      drawerGroup.name,
                                    )}
                                    onToggleDisable={handleToggleDisable}
                                    disableLoading={disableAssetLoading}
                                    onRename={handleRename}
                                    onComment={(name) => setCommentTarget({ category: 'scene', name })}
                                    pendingCommentCount={pendingCommentCountMap?.get(`scene|${drawerGroup.name}`) ?? 0}
                                    availableEpisodes={availableEpisodeOptions}
                                    onEpisodesUpdate={(rawName, state, eps) =>
                                      handleElementEpisodesUpdate(
                                        "scene",
                                        rawName,
                                        state,
                                        eps,
                                      )
                                    }
                                    onGenerateKeywords={onGenerateKeywords}
                                    getKeywordStatus={getKeywordStatus}
                                    onSubmitSearch={onSubmitSearch}
                                    showNoFaceWarning={isRealPerson}
                                  />
                                )}
                              {mobileDrawerAsset &&
                                drawerGroup &&
                                mobileDrawerAsset.type === "prop" && (
                                  <ScenePropGroup
                                    projectId={id}
                                    elementType="prop"
                                    imagePlatform={project?.imagePlatform}
                                    imageModel={project?.imageModel}
                                    baseImagePlatform={
                                      project?.baseImagePlatform
                                    }
                                    baseImageModel={project?.baseImageModel}
                                    imageModels={activeImageModels}
                                    name={drawerGroup.name}
                                    states={
                                      (
                                        drawerGroup as (typeof filteredPropGroups)[0]
                                      ).states
                                    }
                                    baseMetadata={drawerGroup.baseMetadata}
                                    analysisAuthorityRevision={analysisAuthorityRevision}
                                    onUpdate={handleElementUpdate}
                                    onAddState={handlePropAddState}
                                    activeTaskMap={activeTaskMap}
                                    failedTaskMap={failedTaskMap}
                                    baseActiveTaskMap={baseActiveTaskMap}
                                    baseFailedTaskMap={baseFailedTaskMap}
                                    onTaskResolved={onImageTaskResolved}
                                    fixedPrompt={
                                      project?.propFixedPrompt ||
                                      DEFAULT_FIXED_IMAGE_PROMPT
                                    }
                                    projectRefImages={parseRefImages(
                                      project?.propProjectRefImages,
                                    )}
                                    episodeMap={episodeMap}
                                    onBaseGenerated={() =>
                                      id && loadAnalysis(id)
                                    }
                                    onGenerateComplete={() =>
                                      id && loadAnalysis(id)
                                    }
                                    statePromptPrefix={
                                      project?.propStatePromptPrefix
                                    }
                                    onSearchSelect={openSearchSelectModal}
                                    getSearchImageCount={getSearchImageCount}
                                    onSearchSelectAllStates={
                                      openSearchSelectModalAllStates
                                    }
                                    getSearchImageCountAllStates={
                                      getSearchImageCountAllStates
                                    }
                                    getSearchTaskProgress={
                                      getSearchTaskProgress
                                    }
                                    getSearchTaskProgressAllStates={
                                      getSearchTaskProgressAllStates
                                    }
                                    isDisabled={isAssetDisabled(
                                      drawerGroup.name,
                                    )}
                                    onToggleDisable={handleToggleDisable}
                                    disableLoading={disableAssetLoading}
                                    onRename={handleRename}
                                    onComment={(name) => setCommentTarget({ category: 'prop', name })}
                                    pendingCommentCount={pendingCommentCountMap?.get(`prop|${drawerGroup.name}`) ?? 0}
                                    availableEpisodes={availableEpisodeOptions}
                                    onEpisodesUpdate={(rawName, state, eps) =>
                                      handleElementEpisodesUpdate(
                                        "prop",
                                        rawName,
                                        state,
                                        eps,
                                      )
                                    }
                                    onGenerateKeywords={onGenerateKeywords}
                                    getKeywordStatus={getKeywordStatus}
                                    onSubmitSearch={onSubmitSearch}
                                    showNoFaceWarning={isRealPerson}
                                  />
                                )}
                            </MobileAssetDrawer>
                          </>
                        );
                      }

                      /* ── Desktop: original left-nav + cards layout ── */
                      return (
                        <div
                          style={{
                            display: "flex",
                            gap: "0.75rem",
                            alignItems: "flex-start",
                          }}
                        >
                          {/* Left: sticky asset nav */}
                          {navItems.length > 0 && (
                            <div
                              style={{
                                position: "sticky",
                                top: "calc(var(--header-height) + 1rem)",
                                width: "200px",
                                flexShrink: 0,
                                maxHeight:
                                  "calc(100vh - var(--header-height) - 3rem)",
                                display: "flex",
                                flexDirection: "column",
                                paddingRight: "0.5rem",
                              }}
                            >
                              {/* Add new element button at top of nav */}
                              {(() => {
                                const navThemeColor = `var(--theme-${analysisTab === "character" ? "character" : analysisTab === "scene" ? "scene" : "prop"})`;
                                return (
                                  <button
                                    onClick={() => {
                                      const setAdding =
                                        analysisTab === "character"
                                          ? setAddingNewChar
                                          : analysisTab === "scene"
                                            ? setAddingNewScene
                                            : setAddingNewProp;
                                      setAdding({
                                        name: "",
                                        desc: "",
                                        episodes: [],
                                        loading: false,
                                      });
                                      setTimeout(() => {
                                        const rows = document.querySelectorAll(
                                          '[style*="border: 1px solid var(--primary)"]',
                                        );
                                        const last = rows[rows.length - 1];
                                        if (last) {
                                          const headerH =
                                            parseFloat(
                                              getComputedStyle(
                                                document.documentElement,
                                              ).getPropertyValue(
                                                "--header-height",
                                              ),
                                            ) || 58;
                                          const top =
                                            last.getBoundingClientRect().top +
                                            window.scrollY -
                                            headerH -
                                            20;
                                          window.scrollTo({
                                            top,
                                            behavior: "smooth",
                                          });
                                        }
                                      }, 100);
                                    }}
                                    style={{
                                      flexShrink: 0,
                                      background: "none",
                                      border: `1px dashed ${navThemeColor}`,
                                      cursor: "pointer",
                                      textAlign: "center",
                                      padding: "4px 8px",
                                      borderRadius: "4px",
                                      fontSize: "0.7rem",
                                      color: navThemeColor,
                                      lineHeight: 1.3,
                                      whiteSpace: "nowrap",
                                      transition: "all 0.15s",
                                      marginBottom: "4px",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      gap: "2px",
                                    }}
                                    onMouseEnter={(e) => {
                                      e.currentTarget.style.background = `color-mix(in srgb, ${navThemeColor} 12%, transparent)`;
                                    }}
                                    onMouseLeave={(e) => {
                                      e.currentTarget.style.background = "none";
                                    }}
                                  >
                                    <span
                                      style={{
                                        fontSize: "0.85rem",
                                        lineHeight: 1,
                                      }}
                                    >
                                      +
                                    </span>
                                    {analysisTab === "character"
                                      ? t("analysis.addCharacter")
                                      : analysisTab === "scene"
                                        ? t("analysis.addScene")
                                        : t("analysis.addProp")}
                                  </button>
                                );
                              })()}
                              {/* Scrollable nav list（细滚动条 4px=全局 8px 的一半 + 右侧留空隙不贴导航项） */}
                              <div
                                className="thin-scroll"
                                style={{
                                  flex: 1,
                                  minHeight: 0,
                                  overflowY: "auto",
                                  display: "flex",
                                  flexDirection: "column",
                                  gap: "1px",
                                  paddingBottom: "4px",
                                  paddingRight: "6px",
                                }}
                              >
                                {navItems.map((item) => {
                                  const sc = item.disabled
                                    ? navStatusColors.disabled
                                    : navStatusColors[item.status];
                                  const isActive = activeNavId === item.id;
                                  return (
                                    <button
                                      key={item.id}
                                      onClick={() => handleNavClick(item.id)}
                                      style={{
                                        background: isActive
                                          ? "var(--primary)"
                                          : sc.bg,
                                        border: "none",
                                        cursor: "pointer",
                                        flexShrink: 0,
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "3px",
                                        textAlign: "left",
                                        padding: "4px 6px",
                                        borderRadius: "4px",
                                        fontSize: "0.85rem",
                                        color: isActive ? "white" : sc.color,
                                        fontWeight: isActive ? 700 : 500,
                                        lineHeight: 1.3,
                                        transition: "all 0.15s",
                                        textDecoration: item.disabled
                                          ? "line-through"
                                          : "none",
                                        opacity: item.disabled ? 0.6 : 1,
                                        boxShadow: isActive
                                          ? "0 1px 4px rgba(0,0,0,0.15)"
                                          : "none",
                                      }}
                                      onMouseEnter={(e) => {
                                        if (!isActive)
                                          e.currentTarget.style.background =
                                            sc.hoverBg;
                                      }}
                                      onMouseLeave={(e) => {
                                        e.currentTarget.style.background =
                                          isActive ? "var(--primary)" : sc.bg;
                                      }}
                                      title={
                                        item.disabled
                                          ? `${item.name} ${t("character.disabled")}`
                                          : `${item.name}${item.status === "green" ? ` ${t("analysis.navStateImage")}` : item.status === "yellow" ? ` ${t("analysis.navBaseImage")}` : ` ${t("analysis.navNoImage")}`}`
                                      }
                                    >
                                      <span
                                        style={{
                                          flex: 1,
                                          minWidth: 0,
                                          overflow: "hidden",
                                          textOverflow: "ellipsis",
                                          whiteSpace: "nowrap",
                                        }}
                                      >
                                        {item.name}
                                      </span>
                                      {(pendingCommentCountMap?.get(
                                        `${analysisTab}|${item.name}`,
                                      ) ?? 0) > 0 && (
                                        <span
                                          style={{
                                            flexShrink: 0,
                                            width: "7px",
                                            height: "7px",
                                            background: "#ef4444",
                                            borderRadius: "50%",
                                          }}
                                        />
                                      )}
                                      {item.hasNew && !item.disabled && (
                                        <span
                                          style={{
                                            flexShrink: 0,
                                            background: "#22c55e",
                                            color: "#fff",
                                            fontSize: "0.55rem",
                                            fontWeight: 700,
                                            padding: "1px 4px",
                                            borderRadius: "3px",
                                            lineHeight: 1.2,
                                          }}
                                        >
                                          NEW
                                        </span>
                                      )}
                                    </button>
                                  );
                                })}
                              </div>
                              {/* Pinned bottom actions */}
                              <div
                                style={{
                                  flexShrink: 0,
                                  display: "flex",
                                  flexDirection: "column",
                                  gap: "3px",
                                  paddingTop: "4px",
                                }}
                              >
                                <button
                                  onClick={() => {
                                    const el = document.getElementById(
                                      "analysis-section-top",
                                    );
                                    if (el) {
                                      const headerH =
                                        parseFloat(
                                          getComputedStyle(
                                            document.documentElement,
                                          ).getPropertyValue("--header-height"),
                                        ) || 58;
                                      window.scrollTo({
                                        top:
                                          el.getBoundingClientRect().top +
                                          window.scrollY -
                                          headerH -
                                          12,
                                        behavior: "smooth",
                                      });
                                    }
                                  }}
                                  style={{
                                    background: "none",
                                    border: "1px dashed var(--border)",
                                    cursor: "pointer",
                                    textAlign: "center",
                                    padding: "4px 8px",
                                    borderRadius: "4px",
                                    fontSize: "0.7rem",
                                    color: "var(--text-3)",
                                    lineHeight: 1.3,
                                    whiteSpace: "nowrap",
                                    transition: "all 0.15s",
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.background =
                                      "var(--surface-2)";
                                    e.currentTarget.style.color =
                                      "var(--text-2)";
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.background = "none";
                                    e.currentTarget.style.color =
                                      "var(--text-3)";
                                  }}
                                  title={t("analysis.goBackToTopTitle")}
                                >
                                  {t("analysis.goBackToTop")}
                                </button>
                              </div>
                            </div>
                          )}

                          {/* Right: asset cards */}
                          <div
                            style={{
                              flex: 1,
                              minWidth: 0,
                              display: "flex",
                              flexDirection: "column",
                              gap: "23px",
                            }}
                          >
                            {analysisTab === "character" && (
                              <>
                                {filteredCharGroups.length === 0 ? (
                                  <EmptyState
                                    title={t("analysis.noCharacters")}
                                  />
                                ) : (
                                  filteredCharGroups.map((group) => (
                                    <CharacterGroup
                                      key={group.name}
                                      projectId={id}
                                      name={group.name}
                                      states={group.states}
                                      baseMetadata={group.baseMetadata}
                                      analysisAuthorityRevision={analysisAuthorityRevision}
                                      voiceUrl={characterVoiceMap.get(group.name)?.voiceUrl}
                                      basicState={group.basicState}
                                      onUpdate={handleElementUpdate}
                                      onAddState={handleCharAddState}
                                      activeTaskMap={activeTaskMap}
                                      failedTaskMap={failedTaskMap}
                                      baseActiveTaskMap={baseActiveTaskMap}
                                      baseFailedTaskMap={baseFailedTaskMap}
                                      onTaskResolved={onImageTaskResolved}
                                      fixedPrompt={
                                        project?.charFixedPrompt ||
                                        DEFAULT_FIXED_IMAGE_PROMPT
                                      }
                                      projectRefImages={parseRefImages(
                                        project?.charProjectRefImages,
                                      )}
                                      characterImageWorkflow={
                                        effectiveCharacterImageWorkflow
                                      }
                                      imagePlatform={project?.imagePlatform}
                                      imageModel={project?.imageModel}
                                      imageModels={activeImageModels}
                                      episodeMap={episodeMap}
                                      charStatePromptPrefix={
                                        project?.charStatePromptPrefix
                                      }
                                      onBaseGenerated={() =>
                                        id && loadAnalysis(id)
                                      }
                                      onGenerateComplete={() =>
                                        id && loadAnalysis(id)
                                      }
                                      onSearchSelect={openSearchSelectModal}
                                      getSearchImageCount={getSearchImageCount}
                                      onSearchSelectAllStates={
                                        openSearchSelectModalAllStates
                                      }
                                      getSearchImageCountAllStates={
                                        getSearchImageCountAllStates
                                      }
                                      getSearchTaskProgress={
                                        getSearchTaskProgress
                                      }
                                      getSearchTaskProgressAllStates={
                                        getSearchTaskProgressAllStates
                                      }
                                      onNavigateToProp={(propName) => {
                                        setAnalysisTab("prop");
                                        setTimeout(() => {
                                          const el = document.getElementById(
                                            `elem-prop-${propName}`,
                                          );
                                          if (el) {
                                            el.scrollIntoView({
                                              behavior: "smooth",
                                              block: "center",
                                            });
                                            el.style.outline =
                                              "2px solid var(--primary)";
                                            setTimeout(() => {
                                              el.style.outline = "";
                                            }, 2000);
                                          }
                                        }, 100);
                                      }}
                                      allProps={props}
                                      isDisabled={isAssetDisabled(group.name)}
                                      onToggleDisable={handleToggleDisable}
                                      disableLoading={disableAssetLoading}
                                      onRename={handleRename}
                                      onComment={(name) => setCommentTarget({ category: 'character', name })}
                                      pendingCommentCount={pendingCommentCountMap?.get(`character|${group.name}`) ?? 0}
                                      reviewStatusMap={
                                        isRealPerson
                                          ? reviewStatusMap
                                          : undefined
                                      }
                                      onReviewInvalidated={
                                        isRealPerson
                                          ? loadProjectReviews
                                          : undefined
                                      }
                                      availableEpisodes={
                                        availableEpisodeOptions
                                      }
                                      onEpisodesUpdate={(rawName, state, eps) =>
                                        handleElementEpisodesUpdate(
                                          "character",
                                          rawName,
                                          state,
                                          eps,
                                        )
                                      }
                                    />
                                  ))
                                )}
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "0.5rem",
                                  }}
                                >
                                  <AddNewElementRow
                                    label={t("analysis.addCharacter")}
                                    descPlaceholder={t(
                                      "analysis.charDescPlaceholder",
                                    )}
                                    adding={addingNewChar}
                                    setAdding={setAddingNewChar}
                                    onConfirm={(name, desc, eps) =>
                                      handleAddNewElement(
                                        "character",
                                        name,
                                        desc,
                                        eps,
                                        setAddingNewChar,
                                      )
                                    }
                                    availableEpisodes={availableEpisodeOptions}
                                    themeColor="var(--theme-character)"
                                  />
                                  {analysisActionButtons}
                                </div>
                              </>
                            )}
                            {analysisTab === "scene" && (
                              <>
                                {filteredSceneGroups.length === 0 ? (
                                  <EmptyState title={t("analysis.noScenes")} />
                                ) : (
                                  filteredSceneGroups.map((group) => (
                                    <ScenePropGroup
                                      key={group.name}
                                      projectId={id}
                                      elementType="scene"
                                      imagePlatform={project?.imagePlatform}
                                      imageModel={project?.imageModel}
                                      baseImagePlatform={
                                        project?.baseImagePlatform
                                      }
                                      baseImageModel={project?.baseImageModel}
                                      imageModels={activeImageModels}
                                      name={group.name}
                                      states={group.states}
                                      baseMetadata={group.baseMetadata}
                                      analysisAuthorityRevision={analysisAuthorityRevision}
                                      onUpdate={handleElementUpdate}
                                      onAddState={handleSceneAddState}
                                      activeTaskMap={activeTaskMap}
                                      failedTaskMap={failedTaskMap}
                                      baseActiveTaskMap={baseActiveTaskMap}
                                      baseFailedTaskMap={baseFailedTaskMap}
                                      onTaskResolved={onImageTaskResolved}
                                      fixedPrompt={
                                        project?.sceneFixedPrompt ||
                                        DEFAULT_FIXED_IMAGE_PROMPT
                                      }
                                      projectRefImages={parseRefImages(
                                        project?.sceneProjectRefImages,
                                      )}
                                      episodeMap={episodeMap}
                                      onBaseGenerated={() =>
                                        id && loadAnalysis(id)
                                      }
                                      onGenerateComplete={() =>
                                        id && loadAnalysis(id)
                                      }
                                      statePromptPrefix={
                                        project?.sceneStatePromptPrefix
                                      }
                                      onSearchSelect={openSearchSelectModal}
                                      getSearchImageCount={getSearchImageCount}
                                      onSearchSelectAllStates={
                                        openSearchSelectModalAllStates
                                      }
                                      getSearchImageCountAllStates={
                                        getSearchImageCountAllStates
                                      }
                                      getSearchTaskProgress={
                                        getSearchTaskProgress
                                      }
                                      getSearchTaskProgressAllStates={
                                        getSearchTaskProgressAllStates
                                      }
                                      isDisabled={isAssetDisabled(group.name)}
                                      onToggleDisable={handleToggleDisable}
                                      disableLoading={disableAssetLoading}
                                      onRename={handleRename}
                                      onComment={(name) => setCommentTarget({ category: 'scene', name })}
                                      pendingCommentCount={pendingCommentCountMap?.get(`scene|${group.name}`) ?? 0}
                                      availableEpisodes={
                                        availableEpisodeOptions
                                      }
                                      onEpisodesUpdate={(rawName, state, eps) =>
                                        handleElementEpisodesUpdate(
                                          "scene",
                                          rawName,
                                          state,
                                          eps,
                                        )
                                      }
                                      onGenerateKeywords={onGenerateKeywords}
                                      getKeywordStatus={getKeywordStatus}
                                      onSubmitSearch={onSubmitSearch}
                                      showNoFaceWarning={isRealPerson}
                                    />
                                  ))
                                )}
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "0.5rem",
                                  }}
                                >
                                  <AddNewElementRow
                                    label={t("analysis.addScene")}
                                    descPlaceholder={t(
                                      "analysis.sceneDescPlaceholder",
                                    )}
                                    adding={addingNewScene}
                                    setAdding={setAddingNewScene}
                                    onConfirm={(name, desc, eps) =>
                                      handleAddNewElement(
                                        "scene",
                                        name,
                                        desc,
                                        eps,
                                        setAddingNewScene,
                                      )
                                    }
                                    availableEpisodes={availableEpisodeOptions}
                                    themeColor="var(--theme-scene)"
                                  />
                                  {analysisActionButtons}
                                </div>
                              </>
                            )}
                            {analysisTab === "prop" && (
                              <>
                                {filteredPropGroups.length === 0 ? (
                                  <EmptyState title={t("analysis.noProps")} />
                                ) : (
                                  filteredPropGroups.map((group) => (
                                    <ScenePropGroup
                                      key={group.name}
                                      projectId={id}
                                      elementType="prop"
                                      imagePlatform={project?.imagePlatform}
                                      imageModel={project?.imageModel}
                                      baseImagePlatform={
                                        project?.baseImagePlatform
                                      }
                                      baseImageModel={project?.baseImageModel}
                                      imageModels={activeImageModels}
                                      name={group.name}
                                      states={group.states}
                                      baseMetadata={group.baseMetadata}
                                      analysisAuthorityRevision={analysisAuthorityRevision}
                                      onUpdate={handleElementUpdate}
                                      onAddState={handlePropAddState}
                                      activeTaskMap={activeTaskMap}
                                      failedTaskMap={failedTaskMap}
                                      baseActiveTaskMap={baseActiveTaskMap}
                                      baseFailedTaskMap={baseFailedTaskMap}
                                      onTaskResolved={onImageTaskResolved}
                                      fixedPrompt={
                                        project?.propFixedPrompt ||
                                        DEFAULT_FIXED_IMAGE_PROMPT
                                      }
                                      projectRefImages={parseRefImages(
                                        project?.propProjectRefImages,
                                      )}
                                      episodeMap={episodeMap}
                                      onBaseGenerated={() =>
                                        id && loadAnalysis(id)
                                      }
                                      onGenerateComplete={() =>
                                        id && loadAnalysis(id)
                                      }
                                      statePromptPrefix={
                                        project?.propStatePromptPrefix
                                      }
                                      onSearchSelect={openSearchSelectModal}
                                      getSearchImageCount={getSearchImageCount}
                                      onSearchSelectAllStates={
                                        openSearchSelectModalAllStates
                                      }
                                      getSearchImageCountAllStates={
                                        getSearchImageCountAllStates
                                      }
                                      getSearchTaskProgress={
                                        getSearchTaskProgress
                                      }
                                      getSearchTaskProgressAllStates={
                                        getSearchTaskProgressAllStates
                                      }
                                      isDisabled={isAssetDisabled(group.name)}
                                      onToggleDisable={handleToggleDisable}
                                      disableLoading={disableAssetLoading}
                                      onRename={handleRename}
                                      onComment={(name) => setCommentTarget({ category: 'prop', name })}
                                      pendingCommentCount={pendingCommentCountMap?.get(`prop|${group.name}`) ?? 0}
                                      availableEpisodes={
                                        availableEpisodeOptions
                                      }
                                      onEpisodesUpdate={(rawName, state, eps) =>
                                        handleElementEpisodesUpdate(
                                          "prop",
                                          rawName,
                                          state,
                                          eps,
                                        )
                                      }
                                      onGenerateKeywords={onGenerateKeywords}
                                      getKeywordStatus={getKeywordStatus}
                                      onSubmitSearch={onSubmitSearch}
                                      showNoFaceWarning={isRealPerson}
                                    />
                                  ))
                                )}
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "0.5rem",
                                  }}
                                >
                                  <AddNewElementRow
                                    label={t("analysis.addProp")}
                                    descPlaceholder={t(
                                      "analysis.propDescPlaceholder",
                                    )}
                                    adding={addingNewProp}
                                    setAdding={setAddingNewProp}
                                    onConfirm={(name, desc, eps) =>
                                      handleAddNewElement(
                                        "prop",
                                        name,
                                        desc,
                                        eps,
                                        setAddingNewProp,
                                      )
                                    }
                                    availableEpisodes={availableEpisodeOptions}
                                    themeColor="var(--theme-prop)"
                                  />
                                  {analysisActionButtons}
                                </div>
                              </>
                            )}
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                )}
            </div>
          </div>
        )}
    </>
  );
}
